<template>
  <div>
    <v-data-table
      v-model="selectedItems"
      item-key="id"
      :loading="loading"
      :headers="headers"
      :items="computedItems"
      sort-by="number"
      sort-desc
      @click:row="
        (item) =>
          !item.items
            ? $emit('showDetails', item.id)
            : $emit('showGroup', item.id)
      "
      :item-class="() => 'clickable'"
      :show-select="showSelect"
      @toggle-select-all="selectAll"
      v-bind="$attrs"
    >
      <template v-slot:item.number="{ item }">
        {{ item.number }}
        <div class="mt-n1 text--disabled" v-if="item.group">
          {{ item.group.number }}
        </div>
      </template>
      <template v-slot:item.employee="{ item }">
        <template v-if="item.employee"
          ><PersonItem small :value="item.employee" />
          <v-tooltip top v-if="item.department">
            <template v-slot:activator="{ on, attrs }">
              <v-chip small v-bind="attrs" v-on="on">{{
                item.department
              }}</v-chip>
            </template>
            <span>Sammelrechnung FS {{ item.department }} </span>
          </v-tooltip>
        </template>
        <div v-else>
          <v-chip outlined>
            <v-avatar left
              ><v-img :src="require('@/assets/icon.png')" alt="Schulfonds"
            /></v-avatar>
            Schulfonds
          </v-chip>
        </div>
      </template>
      <template v-slot:item.date="{ item }">
        <DateValue :value="item.date" />
      </template>
      <template v-slot:item.enterDate="{ item }">
        <DateValue :value="item.enterDate" />
      </template>
      <template v-slot:item.amount="{ item }">
        <CurrencyValue v-if="item.amount" :value="item.amount" />
        <span class="text--disabled" v-else>CHF ?.??</span>
      </template>
      <template v-slot:item.amountForeign="{ item }">
        <template v-if="!item.items">
          <CurrencyValue
            v-if="!item.currency.local"
            :currency="item.currency.code"
            :value="item.currency.local ? item.amount : item.amountForeign"
          />
          <span v-else class="text--disabled">–</span>
        </template>
      </template>
      <template v-slot:item.expenses="{ item }">
        <CurrencyValue :value="item.expenses" />
      </template>
      <template v-slot:item.actions="{ item }">
        <v-btn icon @click.stop="$emit('showEdit', item.id)"
          ><v-icon>mdi-pen</v-icon></v-btn
        >
      </template>
      <template v-slot:footer.prepend v-if="grouping">
        <v-btn
          outlined
          v-if="selectedIds.length > 1"
          color="info"
          @click="$emit('groupInvoices', selectedIds)"
        >
          {{ selectedIds.length }} Rechnungen gruppieren
        </v-btn>
      </template>
    </v-data-table>
  </div>
</template>
<script>
import { defineComponent } from "vue";
import CurrencyValue from "common/components/CurrencyValue.vue";
import DateValue from "common/components/DateValue.vue";
import PersonItem from "common/components/PersonItem.vue";

export default defineComponent({
  name: "FinanceInvoices",
  components: {
    CurrencyValue,
    DateValue,
    PersonItem,
  },
  props: {
    items: [],
    search: null,
    group: {
      // show grouped items
      type: Boolean,
      default: false,
    },
    grouping: {
      // enable grouping mode
      type: Boolean,
      default: false,
    },
    expenses: {
      type: Boolean,
      default: false,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    showEditBtn: {
      type: Boolean,
      default: false,
    },
    showSelect: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      state: {},
      selectedItems: [],
      headers: [
        { text: "Nummer", value: "number" },
        { text: "Datum", value: "date" },
        { text: "Buchung", value: "enterDate" },
        { text: "Mitarbeiter:in", value: "employee" },
        { text: "Anlass", value: "event" },
        { text: "Beschreibung", value: "description" },
        { text: "Fremdwährung", value: "amountForeign", align: "end" },
        { text: "Betrag", value: "amount", align: "end" },
      ],
    };
  },
  watch: {
    selectedItems(newVal) {
      this.$emit("input", newVal);
    },
    items() {
      this.selectedItems = [];
    },
  },
  computed: {
    selectedIds() {
      return this.selectedItems.map((item) => item.id);
    },
    computedGroupedItems() {
      if (!this.items || this.items.length === 0 || !this.group) {
        return [];
      }
      const result = [];
      for (const item of this.items.filter((item) => item.group)) {
        let group = result.find((el) => el.id === item.group.id);
        if (!group) {
          group = {
            ...item.group,
            date: item.date,
            enterDate: item.enterDate,
            employee: item.employee,
            items: [],
            amount: 0,
            expenses: 0,
            event: "Sammelrechnung",
            isSelectable: false,
          };
          result.push(group);
        }
        group.amount += item.amount;
        if (group.employee) {
          group.employee =
            group.employee.id == item.employee.id ? group.employee : null;
        }
        group.expenses += item.expenses;
        group.date = group.date < item.date ? item.date : group.date;
        group.enterDate =
          group.enterDate < item.enterDate ? item.enterDate : group.enterDate;
        group.items.push(item);
      }

      return result;
    },
    computedItems() {
      if (!this.items || this.items.length === 0) {
        return [];
      }
      const ungroupedItems = this.items
        .filter((item) => !this.group || !item.group)
        .map((item) => {
          let selectable = true;
          if (!item.iban || !item.currency.local || item.noDetails) {
            selectable = false;
          }
          for (const selItem of this.selectedItems) {
            if (item.iban != selItem.iban) {
              selectable = false;
              break;
            }
            if (item.account.code != selItem.account.code) {
              selectable = false;
              break;
            }
          }
          return {
            ...item,
            isSelectable: selectable,
          };
        });
      return [...this.computedGroupedItems, ...ungroupedItems];
    },
  },
  methods: {
    selectAll(value) {
      value.items = value.items.filter((el) => el.isSelectable);
      if (!value.value) {
        this.selectedItems = [];
        return;
      }
      if (value.items.length === 0) {
        this.selectedItems = [];
        return;
      }
      this.selectedItems = [
        value.items[0],
        ...value.items.filter(
          (item) =>
            !!item.iban &&
            item.iban === value.items[0].iban &&
            item.account.code === value.items[0].account.code
        ),
      ];
    },
  },
  created() {
    if (this.expenses) {
      this.headers.push({
        text: "Spesen",
        value: "expenses",
        align: "end",
      });
    }
  },
});
</script>
<style scoped>
:deep(.v-simple-checkbox--disabled) {
  display: none;
}
</style>
